import { Injectable } from '@angular/core';

import { CreateUserModel } from '../models';

/**
 * Keys these values were written to in sessionStorage by older builds. Nothing writes them
 * any more, but `clear()` removes them so a tab loaded on a previous build cannot keep a
 * half-finished sign-up — name, contact details, age range and a live security code — sitting
 * in storage after the student walks away.
 */
const LEGACY_STORAGE_KEYS = [
    'account.preferredLanguage',
    'account.age',
    'account.firstName',
    'account.lastName',
    'account.emailOrPhone',
    'account.receivesNotifications',
    'account.agree',
    'account.securityCode'
];

/**
 * Accumulates the answers given during the sign-up wizard, until they are submitted as one
 * CreateUserModel.
 *
 * Held in memory, deliberately. These values used to be persisted to sessionStorage, where an
 * abandoned sign-up left the student's name, email or phone, age range and security code
 * behind for the life of the tab. Persisting bought nothing in return: the wizard is a dialog
 * opened imperatively, and PopupComponent routes to it with `skipLocationChange: true`, so the
 * URL carries no trace of it and a page reload can never resume it.
 */
@Injectable({
    providedIn: 'root'
})
export class LocalAccountCreationService implements CreateUserModel {
    private readonly values = new Map<string, unknown>();
    private readonly languageKey = 'account.preferredLanguage';
    private readonly ageKey = 'account.age';
    private readonly firstNameKey = 'account.firstName';
    private readonly lastNameKey = 'account.lastName';
    private readonly emailOrPhoneKey = 'account.emailOrPhone';
    private readonly receivesNotificationsKey = 'account.receivesNotifications';
    private readonly agreeKey = 'account.agree';
    private readonly securityCodeKey = 'account.securityCode';

    constructor() {}

    get clientId(): number {
        return runtimeConfig.clientId;
    }

    get complete(): boolean {
        return (
            Boolean(this.preferredLanguageId) &&
            Boolean(this.ageRange) &&
            Boolean(this.firstName) &&
            Boolean(this.lastName) &&
            Boolean(this.emailOrPhoneNumber) &&
            Boolean(this.receivesNotifications) &&
            Boolean(this.acceptedPrivacyPolicy) &&
            Boolean(this.securityCode)
        );
    }

    get preferredLanguageId(): number {
        return this.get(this.languageKey) ?? Number.NaN;
    }

    set preferredLanguageId(value: number) {
        this.set(this.languageKey, value);
    }

    get ageRange(): number {
        return <any>this.get(this.ageKey);
    }

    set ageRange(value: number) {
        this.set(this.ageKey, value);
    }

    get firstName(): string {
        return this.get(this.firstNameKey) || '';
    }

    set firstName(value: string) {
        this.set(this.firstNameKey, value);
    }

    get lastName(): string {
        return this.get(this.lastNameKey) || '';
    }

    set lastName(value: string) {
        this.set(this.lastNameKey, value);
    }

    get emailOrPhoneNumber(): string {
        return this.get(this.emailOrPhoneKey) || '';
    }

    set emailOrPhoneNumber(value: string) {
        this.set(this.emailOrPhoneKey, value);
    }

    get receivesNotifications(): boolean {
        return this.get(this.receivesNotificationsKey) ?? false;
    }

    set receivesNotifications(value: boolean) {
        this.set(this.receivesNotificationsKey, value);
    }

    get acceptedPrivacyPolicy(): boolean {
        return this.get(this.agreeKey) ?? false;
    }

    set acceptedPrivacyPolicy(value: boolean) {
        this.set(this.agreeKey, value);
    }

    get securityCode(): number {
        return this.get(this.securityCodeKey) ?? Number.NaN;
    }

    set securityCode(value: number) {
        this.set(this.securityCodeKey, value);
    }

    createUserModel(): CreateUserModel {
        return {
            acceptedPrivacyPolicy: this.acceptedPrivacyPolicy,
            clientId: this.clientId,
            emailOrPhoneNumber: this.emailOrPhoneNumber,
            firstName: this.firstName,
            lastName: this.lastName,
            preferredLanguageId: this.preferredLanguageId,
            receivesNotifications: this.receivesNotifications,
            securityCode: this.securityCode
        };
    }

    clear(): void {
        this.values.clear();
        LEGACY_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
    }

    private get<T>(key: string): T | null {
        return (this.values.get(key) as T) ?? null;
    }

    private set(key: string, value: any) {
        this.values.set(key, value);
    }
}
